// ===== 1. Imports =====
import React, { useState, useEffect, useMemo } from "react";
import { Eye, Pencil, Trash, Upload, PlusCircle } from "lucide-react";
import Modal from "../components/Modal";
import * as XLSX from "xlsx";
import { db } from "../firebaseConfig";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

// ===== 2. Componente Principal =====
export default function BudgetPage({
  projectCode,
  accounts = [],
  setAccounts,
  pos = [],
  invoices = [],
}) {
  // ===== 3. Estados =====
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [accountForm, setAccountForm] = useState({ code: "", name: "" });
  const [subForm, setSubForm] = useState({ code: "", name: "", budget: 0 });

  // ===== 4. Firestore Sync =====
  useEffect(() => {
    if (!projectCode) return;
    const ref = collection(db, "projects", projectCode, "budget");
    const unsub = onSnapshot(ref, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAccounts(list);
      localStorage.setItem(`budget_${projectCode}`, JSON.stringify(list));
    });
    return () => unsub();
  }, [projectCode]);

  // ===== 5. Helpers =====
  const formatEUR = (num) =>
    (Number(num) || 0).toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €";

  const calcSubTotals = (sub) => {
    const budget = Number(sub.budget) || 0;
    const relatedPOs = (pos || []).filter((po) =>
      (po.items || []).some((it) => it.subaccount === sub.code)
    );
    const validPOs = relatedPOs.filter(
      (po) => po.status !== "Anulada" && po.status !== "Rechazada"
    );
    const committed = validPOs.reduce((sum, po) => {
      const base = (po.items || [])
        .filter((it) => it.subaccount === sub.code)
        .reduce((s, it) => s + (Number(it.base) || 0), 0);
      return sum + base;
    }, 0);
    const realized = (invoices || [])
      .filter((inv) =>
        validPOs.some((po) => po.number === inv.poNumber && inv.baseAmount)
      )
      .reduce((sum, inv) => sum + (Number(inv.baseAmount) || 0), 0);
    const hasPending = validPOs.some((po) =>
      ["Pendiente", "Activa"].includes(po.status)
    );
    return { ...sub, budget, committed, realized, hasPending };
  };

  const getAccountTotals = (account) => {
    const subTotals = (account.subaccounts || []).map(calcSubTotals);
    return {
      subTotals,
      totalBudget: subTotals.reduce((s, x) => s + x.budget, 0),
      totalCommitted: subTotals.reduce((s, x) => s + x.committed, 0),
      totalRealized: subTotals.reduce((s, x) => s + x.realized, 0),
    };
  };

  const getGrandTotals = useMemo(() => {
    return accounts.reduce(
      (totals, acc) => {
        const { totalBudget, totalCommitted, totalRealized } =
          getAccountTotals(acc);
        return {
          budget: totals.budget + totalBudget,
          committed: totals.committed + totalCommitted,
          realized: totals.realized + totalRealized,
        };
      },
      { budget: 0, committed: 0, realized: 0 }
    );
  }, [accounts, pos, invoices]);

  // ===== 6. CRUD =====
  const handleSaveAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.code || !accountForm.name) return;
    try {
      const ref = collection(db, "projects", projectCode, "budget");
      if (editMode?.type === "account") {
        const docRef = doc(ref, editMode.id);
        await setDoc(
          docRef,
          { ...accountForm, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } else {
        const newRef = doc(ref);
        await setDoc(newRef, {
          id: newRef.id,
          ...accountForm,
          subaccounts: [],
          createdAt: serverTimestamp(),
        });
      }
      setAccountForm({ code: "", name: "" });
      setEditMode(null);
      setShowAccountModal(false);
    } catch (err) {
      console.error("Error guardando cuenta:", err);
    }
  };

  const handleSaveSub = async (e) => {
    e.preventDefault();
    if (!subForm.code || !subForm.name || !selectedAccount) return;
    try {
      const updatedSub = {
        id: editMode?.id || Date.now().toString(),
        ...subForm,
        budget: Number(subForm.budget) || 0,
      };
      const updatedAccount = {
        ...selectedAccount,
        subaccounts: selectedAccount.subaccounts
          ? editMode?.type === "sub"
            ? selectedAccount.subaccounts.map((s) =>
                s.id === editMode.id ? updatedSub : s
              )
            : [...selectedAccount.subaccounts, updatedSub]
          : [updatedSub],
        updatedAt: serverTimestamp(),
      };
      await setDoc(
        doc(db, "projects", projectCode, "budget", selectedAccount.id),
        updatedAccount,
        { merge: true }
      );
      setSelectedAccount(updatedAccount);
      setSubForm({ code: "", name: "", budget: 0 });
      setEditMode(null);
      setShowSubModal(false);
    } catch (err) {
      console.error("Error guardando subcuenta:", err);
    }
  };

  // ===== 7. UI =====
  const totals = getGrandTotals;

  // ===== VISTA DETALLE DE CUENTA =====
  if (selectedAccount) {
    const { subTotals, totalBudget, totalCommitted, totalRealized } =
      getAccountTotals(selectedAccount);
    return (
      <section className="container" style={{ padding: "20px 0 30px" }}>
        <button
          className="button"
          onClick={() => setSelectedAccount(null)}
          style={{ marginBottom: 16 }}
        >
          ← Volver
        </button>

        <h2 style={{ marginBottom: 12 }}>
          {selectedAccount.code} – {selectedAccount.name}
        </h2>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <button
            className="button button--primary"
            onClick={() => {
              setEditMode(null);
              setSubForm({ code: "", name: "", budget: 0 });
              setShowSubModal(true);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 32,
              padding: "6px 10px",
              fontSize: 13,
            }}
          >
            <PlusCircle size={15} />
            Nueva subcuenta
          </button>
        </div>

        <table
          className="users-table"
          style={{ width: "100%", marginBottom: 16 }}
        >
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th>Presupuesto</th>
              <th>Comprometido</th>
              <th>Realizado</th>
              <th>Comp + Real</th>
              <th>Var</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {subTotals.map((s) => {
              const variation = s.budget - (s.committed + s.realized);
              return (
                <tr key={s.id}>
                  <td>{s.code}</td>
                  <td>{s.name}</td>
                  <td>{formatEUR(s.budget)}</td>
                  <td style={{ color: s.hasPending ? "#eab308" : "#111827" }}>
                    {formatEUR(s.committed)}
                  </td>
                  <td style={{ color: "var(--accent)" }}>
                    {formatEUR(s.realized)}
                  </td>
                  <td>{formatEUR(s.committed + s.realized)}</td>
                  <td
                    style={{
                      color: variation < 0 ? "#dc2626" : "#16a34a",
                      fontWeight: 500,
                    }}
                  >
                    {formatEUR(variation)}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="button button--ghost"
                        onClick={() => {
                          setSubForm(s);
                          setEditMode({ type: "sub", id: s.id });
                          setShowSubModal(true);
                        }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="button button--ghost"
                        onClick={() =>
                          setSelectedAccount((prev) => ({
                            ...prev,
                            subaccounts: prev.subaccounts.filter(
                              (x) => x.id !== s.id
                            ),
                          }))
                        }
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 32,
            fontWeight: 600,
          }}
        >
          <div>Presupuesto: {formatEUR(totalBudget)}</div>
          <div style={{ color: "#eab308" }}>
            Comprometido: {formatEUR(totalCommitted)}
          </div>
          <div style={{ color: "var(--accent)" }}>
            Realizado: {formatEUR(totalRealized)}
          </div>
        </div>

        {/* MODAL SUBCUENTA */}
        {showSubModal && (
          <Modal
            title={
              editMode?.type === "sub" ? "Editar subcuenta" : "Nueva subcuenta"
            }
            onClose={() => setShowSubModal(false)}
          >
            <form className="stack" onSubmit={handleSaveSub}>
              <div className="form-grid">
                <div className="field">
                  <label>Código</label>
                  <input
                    type="text"
                    value={subForm.code}
                    onChange={(e) =>
                      setSubForm({ ...subForm, code: e.target.value })
                    }
                  />
                </div>
                <div className="field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={subForm.name}
                    onChange={(e) =>
                      setSubForm({ ...subForm, name: e.target.value })
                    }
                  />
                </div>
                <div className="field">
                  <label>Presupuesto</label>
                  <input
                    type="number"
                    value={subForm.budget}
                    onChange={(e) =>
                      setSubForm({
                        ...subForm,
                        budget: Number(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <button
                  type="button"
                  className="button"
                  onClick={() => setShowSubModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="button button--primary">
                  Guardar
                </button>
              </div>
            </form>
          </Modal>
        )}
      </section>
    );
  }

  // ===== VISTA GENERAL =====
  return (
    <section className="container" style={{ padding: "20px 0 30px" }}>
      {/* Botones Nueva / Importar */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
          marginBottom: 16,
        }}
      >
        <button
          className="button button--primary"
          title="Nueva cuenta"
          onClick={() => {
            setEditMode(null);
            setAccountForm({ code: "", name: "" });
            setShowAccountModal(true);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "6px 10px",
            fontSize: 13,
          }}
        >
          <PlusCircle size={15} />
          Nueva cuenta
        </button>

        <button
          className="button"
          title="Importar presupuesto"
          onClick={() => setShowImportModal(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "6px 10px",
            fontSize: 13,
          }}
        >
          <Upload size={15} />
          Importar
        </button>
      </div>

      {/* Gran total */}
      <div
        className="card"
        style={{
          marginBottom: 24,
          padding: 20,
          fontWeight: 600,
        }}
      >
        Gran total del presupuesto: {formatEUR(totals.budget)}
      </div>

      {/* Cuentas */}
      <div className="stack">
        {accounts.map((acc) => {
          const { totalBudget, totalCommitted, totalRealized } =
            getAccountTotals(acc);
          return (
            <div key={acc.id} className="card" style={{ padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: 0 }}>
                  {acc.code} – {acc.name}
                </h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="button button--ghost"
                    onClick={() => setSelectedAccount(acc)}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    className="button button--ghost"
                    onClick={() => {
                      setAccountForm({ code: acc.code, name: acc.name });
                      setEditMode({ type: "account", id: acc.id });
                      setShowAccountModal(true);
                    }}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="button button--ghost"
                    onClick={() => handleDeleteAccount(acc)}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 14, marginTop: 6 }}>
                Presupuesto: {formatEUR(totalBudget)} –{" "}
                <span style={{ color: "#eab308" }}>
                  Comprometido: {formatEUR(totalCommitted)}
                </span>{" "}
                –{" "}
                <span style={{ color: "var(--accent)" }}>
                  Realizado: {formatEUR(totalRealized)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modales generales */}
      {showAccountModal && (
        <Modal
          title={
            editMode?.type === "account" ? "Editar cuenta" : "Nueva cuenta"
          }
          onClose={() => setShowAccountModal(false)}
        >
          <form className="stack" onSubmit={handleSaveAccount}>
            <div className="form-grid">
              <div className="field">
                <label>Código</label>
                <input
                  type="text"
                  value={accountForm.code}
                  onChange={(e) =>
                    setAccountForm({ ...accountForm, code: e.target.value })
                  }
                />
              </div>
              <div className="field">
                <label>Nombre</label>
                <input
                  type="text"
                  value={accountForm.name}
                  onChange={(e) =>
                    setAccountForm({ ...accountForm, name: e.target.value })
                  }
                />
              </div>
            </div>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                type="button"
                className="button"
                onClick={() => setShowAccountModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="button button--primary">
                Guardar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showImportModal && (
        <Modal
          title="Importar presupuesto"
          onClose={() => setShowImportModal(false)}
        >
          <div className="stack">
            <div className="field">
              <label>Plantilla de importación</label>
              <button className="button" onClick={handleDownloadTemplate}>
                Descargar plantilla
              </button>
            </div>
            <div className="field">
              <label>Subir archivo</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFile}
              />
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
